import { useCallback, useMemo, useState } from 'react';
import { timeAndLeaveGateway } from '../index';
import type { EscoHrHolidayCalendarResponse, EscoHrHolidayRequestResponse, EscoHrWorkforceSummaryResponse } from '../types';
import { RetryableError } from '../../../shared/RetryableError';
import { usePermissionCheck } from '../../identity/state/usePermissions';
import { EscoHrConnectionBanner, SyntheticBadge } from './escoHrWorkforceShared';
import { hasEscoHrData, humanize, useEmployeeNames, useEscoHrLoad } from './escoHrWorkforceHooks';

const HOLIDAYS_READ = 'holidays.read';

interface HolidaysData {
  summary: EscoHrWorkforceSummaryResponse;
  calendar: EscoHrHolidayCalendarResponse[];
  requests: EscoHrHolidayRequestResponse[];
}

/**
 * The holiday calendar and holiday requests from escoHR, read-only. escoHR owns the dates, observances and floating-holiday requests; EPN owns the
 * payroll rules (paid or unpaid, hours, premium), which are not built yet, so no holiday is paid automatically and nothing here is used in payroll.
 */
export function EscoHrHolidaysSection() {
  const can = usePermissionCheck();
  const allowed = can(HOLIDAYS_READ);
  const load = useCallback(async (signal: AbortSignal): Promise<HolidaysData> => {
    const [summary, calendar, requests] = await Promise.all([
      timeAndLeaveGateway.getEscoHrHolidaysSummary(signal),
      timeAndLeaveGateway.listEscoHrHolidayCalendar(signal),
      timeAndLeaveGateway.listEscoHrHolidayRequests(signal),
    ]);
    return { summary, calendar, requests };
  }, []);
  const { data, error, reload } = useEscoHrLoad(allowed, load);
  const employeeName = useEmployeeNames(allowed && data !== null);

  const [year, setYear] = useState('');
  const [status, setStatus] = useState('');

  const calendar = data?.calendar;
  const requests = data?.requests;
  const visibleCalendar = useMemo(() => (calendar ?? []).filter((h) => !year || String(h.year) === year), [calendar, year]);
  const visibleRequests = useMemo(() => (requests ?? []).filter((r) => !status || r.status === status), [requests, status]);

  if (!allowed) {
    return <section className="card card--wide"><p className="muted">You do not have permission to view holidays from escoHR.</p></section>;
  }

  if (error) {
    return <section className="card card--wide" data-testid="escohr-holidays-error"><RetryableError message={error} onRetry={reload} /></section>;
  }

  if (!data) {
    return <section className="card card--wide" aria-busy="true"><p className="muted">Loading holidays from escoHR…</p></section>;
  }

  const years = [...new Set(data.calendar.map((h) => h.year))].sort();
  const statuses = [...new Set(data.requests.map((r) => r.status))].sort();

  return (
    <div data-testid="escohr-holidays">
      <EscoHrConnectionBanner summary={data.summary} heading="escoHR Holidays" />
      {hasEscoHrData(data.summary) && (
        <>
          <section className="card card--wide" aria-label="escoHR holiday calendar">
            <h4>Holiday calendar from escoHR</h4>
            <p className="muted">
              escoHR supplies the dates and observances. Whether a holiday is paid, for how many hours and for whom is set in EPN and is not built yet, so no holiday is paid automatically and nothing here is used in payroll.
            </p>
            {data.calendar.length > 0 && (
              <div className="inline-form">
                <select aria-label="Filter by year" value={year} onChange={(e) => setYear(e.target.value)}>
                  <option value="">All years</option>
                  {years.map((y) => <option key={y} value={String(y)}>{y}</option>)}
                </select>
              </div>
            )}
            <div style={{ overflowX: 'auto', contain: 'inline-size' }}>
              <table aria-label="escoHR holiday calendar">
                <thead><tr><th>Holiday</th><th>Observed on</th><th>Applies to</th><th></th></tr></thead>
                <tbody>
                  {visibleCalendar.map((h) => (
                    <tr key={h.id}>
                      <td>{h.name}</td>
                      <td>{h.observedDate}</td>
                      <td>{h.locationRefs.length === 0 ? 'Whole company' : h.locationRefs.join(', ')}</td>
                      <td>{h.isSynthetic && <SyntheticBadge />}</td>
                    </tr>
                  ))}
                  {data.calendar.length === 0 && <tr><td colSpan={4} className="muted">No holiday calendar has been received from escoHR yet.</td></tr>}
                  {data.calendar.length > 0 && visibleCalendar.length === 0 && <tr><td colSpan={4} className="muted">No holidays match this year.</td></tr>}
                </tbody>
              </table>
            </div>
          </section>

          <section className="card card--wide" aria-label="escoHR holiday requests">
            <h4>Holiday requests from escoHR</h4>
            <p className="muted">Floating-holiday, observance and schedule-exception requests. escoHR approves them; EPN does not.</p>
            {data.requests.length > 0 && (
              <div className="inline-form">
                <select aria-label="Filter requests by status" value={status} onChange={(e) => setStatus(e.target.value)}>
                  <option value="">All statuses</option>
                  {statuses.map((s) => <option key={s} value={s}>{humanize(s)}</option>)}
                </select>
              </div>
            )}
            <div style={{ overflowX: 'auto', contain: 'inline-size' }}>
              <table aria-label="escoHR holiday requests">
                <thead><tr><th>Employee</th><th>Requested date</th><th>Holiday or observance</th><th>Hours</th><th>Status</th><th></th></tr></thead>
                <tbody>
                  {visibleRequests.map((r) => (
                    <tr key={r.id}>
                      <td>{employeeName(r.epnEmployeeId)}</td>
                      <td>{r.requestedDate}</td>
                      <td>{r.observance ?? r.holidayRef ?? '—'}</td>
                      <td>{r.hours === null ? '—' : `${r.hours} h`}</td>
                      <td>{humanize(r.status)}</td>
                      <td>{r.isSynthetic && <SyntheticBadge />}</td>
                    </tr>
                  ))}
                  {data.requests.length === 0 && <tr><td colSpan={6} className="muted">No holiday requests have been received from escoHR yet.</td></tr>}
                  {data.requests.length > 0 && visibleRequests.length === 0 && <tr><td colSpan={6} className="muted">No requests match this status.</td></tr>}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
